import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuthState } from '@/components/feature/AuthGuard';

type AuthMode = 'login' | 'signup' | 'forgot';

function buildAccountUrl(returnTo?: string): string {
  const basePath = (__BASE_PATH__ as string).split('/').filter(Boolean).join('/');
  const pathPrefix = basePath ? `/${basePath}` : '';
  const accountUrl = `${window.location.origin}${pathPrefix}/account`;
  return returnTo ? `${accountUrl}?returnTo=${encodeURIComponent(returnTo)}` : accountUrl;
}

function maskEmail(email: string): string {
  const [local, domain] = email.split('@');
  if (!domain) return email;
  const visible = local.slice(0, 2);
  return `${visible}••••@${domain}`;
}

const SPECIAL_RE = /[!@#$%^&*()_+\-=[\];':"\\|,.<>/?`~]/;

interface PasswordChecks {
  length: boolean;
  letter: boolean;
  number: boolean;
  special: boolean;
}

function getPasswordChecks(password: string): PasswordChecks {
  return {
    length: password.length >= 8,
    letter: /[A-Za-z]/.test(password),
    number: /[0-9]/.test(password),
    special: SPECIAL_RE.test(password),
  };
}

function PasswordEyeButton({
  visible,
  onToggle,
}: {
  visible: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-label={visible ? '隱藏密碼' : '顯示密碼'}
      className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center rounded-md text-foreground-400 hover:text-foreground-700 transition-colors cursor-pointer"
    >
      <i className={`${visible ? 'ri-eye-off-line' : 'ri-eye-line'} text-base`} />
    </button>
  );
}

function CheckItem({ ok, label }: { ok: boolean; label: string }) {
  return (
    <li className={`flex items-center gap-2 text-xs ${ok ? 'text-emerald-600' : 'text-foreground-400'}`}>
      <i className={`${ok ? 'ri-checkbox-circle-fill' : 'ri-checkbox-blank-circle-line'} text-sm`} />
      <span>{label}</span>
    </li>
  );
}

export default function AccountPage() {
  const auth = useAuthState();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get('returnTo') || '/';
  const [mode, setMode] = useState<AuthMode>('login');

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [signupConfirmPassword, setSignupConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const [emailVerified, setEmailVerified] = useState(false);

  // 註冊成功後、等待 Email 驗證的畫面
  const [signupState, setSignupState] = useState<'idle' | 'pending'>('idle');
  const [pendingEmail, setPendingEmail] = useState('');
  const [resending, setResending] = useState(false);
  const [resendMsg, setResendMsg] = useState<string | null>(null);
  const [resendCountdown, setResendCountdown] = useState(0);
  const [rechecking, setRechecking] = useState(false);

  // 密碼重設（recovery）流程
  const [recoveryMode, setRecoveryMode] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const isRecovery =
      params.get('type') === 'recovery' || window.location.hash.includes('type=recovery');
    if (isRecovery) setRecoveryMode(true);
  }, []);

  useEffect(() => {
    if (auth.user) {
      setEmailVerified(!!auth.user.email_confirmed_at);
    }
  }, [auth.user]);

  // 60 秒重新寄送倒數
  useEffect(() => {
    if (resendCountdown <= 0) return;
    const t = setTimeout(() => setResendCountdown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCountdown]);

  const safeReturnTo = returnTo.startsWith('/') && !returnTo.startsWith('//') ? returnTo : '/';

  const passwordChecks = getPasswordChecks(password);
  const passwordsMatch =
    signupConfirmPassword.length > 0 && password === signupConfirmPassword;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccessMsg(null);

    if (mode === 'forgot') {
      if (!email.trim()) {
        setError('請輸入你的電子郵件。');
        return;
      }
      setLoading(true);
      try {
        const { error: resetErr } = await supabase.auth.resetPasswordForEmail(email.trim(), {
          redirectTo: buildAccountUrl(),
        });
        if (resetErr) throw resetErr;
        setSuccessMsg('重設密碼連結已寄出，請檢查信箱（含垃圾郵件匣）。');
      } catch (err) {
        setError(err instanceof Error ? err.message : '寄送失敗');
      } finally {
        setLoading(false);
      }
      return;
    }

    if (mode === 'signup') {
      handleSignup();
      return;
    }

    // 登入
    if (!email.trim() || !password) {
      setError('請填寫電子郵件與密碼。');
      return;
    }
    setLoading(true);
    try {
      const { error: signInErr } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password,
      });
      if (signInErr) throw signInErr;
      navigate(safeReturnTo, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : '認證失敗');
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async () => {
    if (!email.trim()) {
      setError('請輸入電子郵件。');
      return;
    }
    if (!passwordChecks.length || !passwordChecks.letter || !passwordChecks.number || !passwordChecks.special) {
      setError('密碼需至少 8 字元，並包含英文字母、數字與特殊符號。');
      return;
    }
    if (!signupConfirmPassword) {
      setError('請再次輸入密碼確認。');
      return;
    }
    if (password !== signupConfirmPassword) {
      setError('兩次輸入的密碼不一致。');
      return;
    }

    setLoading(true);
    try {
      const { data, error: signUpErr } = await supabase.auth.signUp({
        email: email.trim(),
        password,
        options: { emailRedirectTo: buildAccountUrl(safeReturnTo) },
      });
      if (signUpErr) throw signUpErr;

      if (data.session) {
        // Auth 未啟用 Email 驗證，註冊後直接登入
        navigate(safeReturnTo, { replace: true });
      } else {
        // Auth 已啟用 Email 驗證，等待驗證信
        setPendingEmail(email.trim());
        setSignupState('pending');
        setResendCountdown(60);
        setResendMsg(null);
        setPassword('');
        setSignupConfirmPassword('');
        setShowPassword(false);
        setShowConfirmPassword(false);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : '註冊失敗');
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!pendingEmail || resendCountdown > 0 || resending) return;
    setResending(true);
    setResendMsg(null);
    try {
      const { error } = await supabase.auth.resend({ type: 'signup', email: pendingEmail });
      if (error) throw error;
      setResendMsg('驗證信已重新寄出，請檢查信箱（含垃圾郵件匣）。');
      setResendCountdown(60);
    } catch (err) {
      setResendMsg(err instanceof Error ? err.message : '寄送失敗');
    } finally {
      setResending(false);
    }
  };

  const handleRecheck = async () => {
    setRechecking(true);
    setResendMsg(null);
    try {
      const { data } = await supabase.auth.getSession();
      if (data.session?.user?.email_confirmed_at) {
        navigate(safeReturnTo, { replace: true });
        return;
      }
      setResendMsg('尚未偵測到驗證完成，請先到信箱點擊驗證連結後再試。');
    } catch (err) {
      setResendMsg(err instanceof Error ? err.message : '檢查失敗');
    } finally {
      setRechecking(false);
    }
  };

  const handleResendWhileLoggedIn = async () => {
    if (!auth.user?.email) return;
    setResending(true);
    setResendMsg(null);
    try {
      const { error } = await supabase.auth.resend({ type: 'signup', email: auth.user.email });
      if (error) throw error;
      setResendMsg('驗證信已重新寄出，請檢查信箱（含垃圾郵件匣）。');
    } catch (err) {
      setResendMsg(err instanceof Error ? err.message : '寄送失敗');
    } finally {
      setResending(false);
    }
  };

  const handleUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccessMsg(null);
    if (newPassword.length < 8) {
      setError('密碼至少需要 8 個字元。');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('兩次輸入的密碼不一致。');
      return;
    }
    setLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) throw error;
      setSuccessMsg('密碼已更新。');
      setRecoveryMode(false);
      setNewPassword('');
      setConfirmPassword('');
      navigate(safeReturnTo, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : '更新密碼失敗');
    } finally {
      setLoading(false);
    }
  };

  const inputClass =
    'w-full px-3 py-2.5 rounded-md border border-foreground-200 bg-background-50 text-sm text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-accent-300';

  return (
    <div className="min-h-screen bg-background-50 text-foreground-900">
      <header className="border-b border-foreground-200/50 bg-background-50">
        <div className="max-w-5xl mx-auto px-4 md:px-6 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5 whitespace-nowrap cursor-pointer">
            <span className="w-8 h-8 rounded-lg bg-accent-500 flex items-center justify-center">
              <i className="ri-shield-user-line text-base text-background-50" />
            </span>
            <span className="text-base tracking-wide text-foreground-800" style={{ fontFamily: 'var(--font-heading)' }}>
              AUM1001 帳號
            </span>
          </Link>
          <Link
            to="/"
            className="whitespace-nowrap text-xs text-foreground-500 hover:text-foreground-800 transition-colors cursor-pointer"
          >
            返回首頁
          </Link>
        </div>
      </header>

      <main className="max-w-md mx-auto px-4 md:px-6 py-10 md:py-16">
        {auth.loading ? (
          <div className="py-20 text-center space-y-3">
            <div className="w-10 h-10 mx-auto border-2 border-accent-300 border-t-accent-500 rounded-full animate-spin" />
            <p className="text-sm text-foreground-500">載入中……</p>
          </div>
        ) : auth.user ? (
          recoveryMode ? (
            <div className="bg-background-100 border border-foreground-200/40 rounded-xl p-6 md:p-8 space-y-5">
              <div className="text-center">
                <span className="inline-flex w-11 h-11 rounded-full bg-amber-100 items-center justify-center">
                  <i className="ri-lock-password-line text-xl text-amber-600" />
                </span>
                <h1 className="text-lg text-foreground-800 mt-3 mb-1" style={{ fontFamily: 'var(--font-heading)' }}>
                  設定新密碼
                </h1>
                <p className="text-sm text-foreground-600">{auth.user.email}</p>
              </div>

              <form onSubmit={handleUpdatePassword} className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-foreground-600 mb-1">新密碼</label>
                  <div className="relative">
                    <input
                      type={showNewPassword ? 'text' : 'password'}
                      name="newPassword"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                      minLength={8}
                      placeholder="至少 8 個字元"
                      className={`${inputClass} pr-10`}
                    />
                    <PasswordEyeButton visible={showNewPassword} onToggle={() => setShowNewPassword((v) => !v)} />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground-600 mb-1">再次輸入新密碼</label>
                  <div className="relative">
                    <input
                      type={showConfirmNewPassword ? 'text' : 'password'}
                      name="confirmPassword"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      minLength={8}
                      placeholder="再次輸入新密碼"
                      className={`${inputClass} pr-10`}
                    />
                    <PasswordEyeButton visible={showConfirmNewPassword} onToggle={() => setShowConfirmNewPassword((v) => !v)} />
                  </div>
                </div>

                {error && <p className="text-xs text-rose-600 bg-rose-50 rounded-md px-3 py-2">{error}</p>}
                {successMsg && <p className="text-xs text-emerald-600 bg-emerald-50 rounded-md px-3 py-2">{successMsg}</p>}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full whitespace-nowrap px-4 py-2.5 rounded-md bg-accent-500 text-background-50 text-sm hover:bg-accent-600 transition-colors cursor-pointer disabled:opacity-50"
                >
                  {loading ? '更新中……' : '更新密碼'}
                </button>
              </form>
            </div>
          ) : (
            <div className="bg-background-100 border border-foreground-200/40 rounded-xl p-6 md:p-8 space-y-5">
              <div className="text-center">
                <span className="inline-flex w-11 h-11 rounded-full bg-emerald-100 items-center justify-center">
                  <i className="ri-user-3-line text-xl text-emerald-600" />
                </span>
                <h1 className="text-lg text-foreground-800 mt-3 mb-1" style={{ fontFamily: 'var(--font-heading)' }}>
                  已登入 AUM1001
                </h1>
                <p className="text-sm text-foreground-600">{auth.user.email}</p>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between gap-3 rounded-lg border border-foreground-200/50 bg-background-50 px-4 py-3">
                  <div className="flex items-center gap-2">
                    <i
                      className={`${emailVerified ? 'ri-checkbox-circle-fill text-emerald-500' : 'ri-alert-line text-amber-500'}`}
                    />
                    <span className="text-sm text-foreground-700">Email 驗證狀態</span>
                  </div>
                  <span
                    className={`text-xs px-2.5 py-1 rounded-full whitespace-nowrap ${
                      emailVerified ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
                    }`}
                  >
                    {emailVerified ? '已驗證' : '未驗證'}
                  </span>
                </div>

                {!emailVerified && (
                  <div className="space-y-2">
                    <p className="text-xs text-foreground-500 leading-relaxed">
                      你的 Email 尚未完成驗證。部分功能（如建立施炁紀錄、取得 ADM 授權）需要先完成驗證。
                    </p>
                    <button
                      onClick={handleResendWhileLoggedIn}
                      disabled={resending}
                      className="w-full whitespace-nowrap px-4 py-2 rounded-md border border-accent-300 text-accent-700 text-sm hover:bg-accent-50 transition-colors cursor-pointer disabled:opacity-50"
                    >
                      {resending ? '寄送中……' : '重新寄送驗證信'}
                    </button>
                    {resendMsg && <p className="text-xs text-foreground-500">{resendMsg}</p>}
                  </div>
                )}

                <div className="flex items-center gap-3 flex-wrap pt-1">
                  <button
                    onClick={() => navigate(safeReturnTo, { replace: true })}
                    className="flex-1 whitespace-nowrap px-4 py-2.5 rounded-md bg-accent-500 text-background-50 text-sm hover:bg-accent-600 transition-colors cursor-pointer"
                  >
                    返回原頁面
                  </button>
                  <button
                    onClick={auth.signOut}
                    className="flex-1 whitespace-nowrap px-4 py-2.5 rounded-md border border-foreground-200 text-sm text-foreground-600 hover:bg-foreground-50 transition-colors cursor-pointer"
                  >
                    登出
                  </button>
                </div>
              </div>
            </div>
          )
        ) : signupState === 'pending' ? (
          <div className="bg-background-100 border border-foreground-200/40 rounded-xl p-6 md:p-8 space-y-5 text-center">
            <span className="inline-flex w-12 h-12 rounded-full bg-emerald-100 items-center justify-center">
              <i className="ri-mail-send-line text-2xl text-emerald-600" />
            </span>
            <div>
              <h1 className="text-lg text-foreground-800 mb-2" style={{ fontFamily: 'var(--font-heading)' }}>
                帳號已建立
              </h1>
              <p className="text-sm text-foreground-600 leading-relaxed">
                請到 Email 收取 AUM1001Matrix.org 驗證信，完成驗證後即可登入。
              </p>
            </div>

            <div className="rounded-lg border border-foreground-200/50 bg-background-50 px-4 py-3">
              <p className="text-xs text-foreground-400 mb-1">驗證信已寄送至</p>
              <p className="text-sm font-medium text-foreground-800 break-all">{maskEmail(pendingEmail)}</p>
            </div>

            {resendMsg && (
              <p
                className={`text-xs rounded-md px-3 py-2 ${
                  resendMsg.includes('已') ? 'text-emerald-600 bg-emerald-50' : 'text-rose-600 bg-rose-50'
                }`}
              >
                {resendMsg}
              </p>
            )}

            <div className="space-y-2">
              <button
                onClick={handleResendVerification}
                disabled={resending || resendCountdown > 0}
                className="w-full whitespace-nowrap px-4 py-2.5 rounded-md border border-accent-300 text-accent-700 text-sm hover:bg-accent-50 transition-colors cursor-pointer disabled:opacity-50"
              >
                {resending
                  ? '寄送中……'
                  : resendCountdown > 0
                    ? `重新寄送驗證信（${resendCountdown} 秒）`
                    : '重新寄送驗證信'}
              </button>
              <button
                onClick={handleRecheck}
                disabled={rechecking}
                className="w-full whitespace-nowrap px-4 py-2.5 rounded-md bg-accent-500 text-background-50 text-sm hover:bg-accent-600 transition-colors cursor-pointer disabled:opacity-50"
              >
                {rechecking ? '檢查中……' : '我已完成驗證，重新檢查'}
              </button>
              <button
                onClick={() => {
                  setSignupState('idle');
                  setPendingEmail('');
                  setResendMsg(null);
                  setMode('login');
                }}
                className="w-full whitespace-nowrap px-4 py-2.5 rounded-md border border-foreground-200 text-sm text-foreground-600 hover:bg-foreground-50 transition-colors cursor-pointer"
              >
                返回登入
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-5">
            <div className="text-center">
              <span className="inline-flex w-11 h-11 rounded-lg bg-accent-500 items-center justify-center">
                <i className="ri-shield-user-line text-xl text-background-50" />
              </span>
              <h1 className="text-lg text-foreground-800 mt-3 mb-1" style={{ fontFamily: 'var(--font-heading)' }}>
                {mode === 'signup' ? '建立 AUM1001 帳號' : '登入 AUM1001'}
              </h1>
              <p className="text-xs text-foreground-500 leading-relaxed">
                全站統一帳號。RG、OG、宇炁療育與 ADM 管理入口共用同一組帳號與密碼。
              </p>
            </div>

            <div className="flex p-1 rounded-full bg-background-100 border border-foreground-200/50">
              {(['login', 'signup', 'forgot'] as AuthMode[]).map((m) => (
                <button
                  key={m}
                  onClick={() => {
                    setMode(m);
                    setError(null);
                    setSuccessMsg(null);
                  }}
                  className={`flex-1 whitespace-nowrap px-3 py-1.5 rounded-full text-sm transition-colors cursor-pointer ${
                    mode === m
                      ? 'bg-background-50 text-foreground-900 border border-foreground-200/60'
                      : 'text-foreground-500 hover:text-foreground-800'
                  }`}
                >
                  {m === 'login' ? '登入' : m === 'signup' ? '註冊' : '忘記密碼'}
                </button>
              ))}
            </div>

            <div className="bg-background-100 border border-foreground-200/40 rounded-xl p-6 md:p-8">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-foreground-600 mb-1">電子郵件</label>
                  <input
                    type="email"
                    name="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                    placeholder="your@email.com"
                    className={inputClass}
                  />
                </div>

                {mode !== 'forgot' && (
                  <div>
                    <label className="block text-xs font-medium text-foreground-600 mb-1">密碼</label>
                    <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        name="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
                        placeholder={mode === 'signup' ? '至少 8 字元，含字母、數字與特殊符號' : '請輸入密碼'}
                        className={`${inputClass} pr-10`}
                      />
                      <PasswordEyeButton visible={showPassword} onToggle={() => setShowPassword((v) => !v)} />
                    </div>
                  </div>
                )}

                {mode === 'signup' && (
                  <>
                    <div>
                      <label className="block text-xs font-medium text-foreground-600 mb-1">確認密碼</label>
                      <div className="relative">
                        <input
                          type={showConfirmPassword ? 'text' : 'password'}
                          name="confirmPassword"
                          value={signupConfirmPassword}
                          onChange={(e) => setSignupConfirmPassword(e.target.value)}
                          required
                          autoComplete="new-password"
                          placeholder="再次輸入相同密碼"
                          className={`${inputClass} pr-10`}
                        />
                        <PasswordEyeButton visible={showConfirmPassword} onToggle={() => setShowConfirmPassword((v) => !v)} />
                      </div>
                    </div>

                    <ul className="grid grid-cols-1 gap-1.5 rounded-lg border border-foreground-200/40 bg-background-50 px-3 py-3">
                      <CheckItem ok={passwordChecks.length} label="長度至少 8 字元" />
                      <CheckItem ok={passwordChecks.letter} label="包含英文字母" />
                      <CheckItem ok={passwordChecks.number} label="包含數字" />
                      <CheckItem ok={passwordChecks.special} label="包含特殊符號（如 @ ! # $ %）" />
                      <CheckItem ok={passwordsMatch} label="兩次密碼一致" />
                    </ul>
                  </>
                )}

                {error && <p className="text-xs text-rose-600 bg-rose-50 rounded-md px-3 py-2">{error}</p>}
                {successMsg && <p className="text-xs text-emerald-600 bg-emerald-50 rounded-md px-3 py-2">{successMsg}</p>}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full whitespace-nowrap px-4 py-2.5 rounded-md bg-accent-500 text-background-50 text-sm hover:bg-accent-600 transition-colors cursor-pointer disabled:opacity-50"
                >
                  {loading
                    ? '處理中……'
                    : mode === 'login'
                      ? '登入'
                      : mode === 'signup'
                        ? '建立帳號'
                        : '寄送重設密碼連結'}
                </button>
              </form>
            </div>

            <p className="text-xs text-foreground-400 text-center leading-relaxed">
              註冊即建立全站 AUM1001 帳號，與 RG、OG、宇炁療育、ADM 管理共用同一組登入憑證。
            </p>
          </div>
        )}
      </main>
    </div>
  );
}